import CopyableField from '@src/components/atoms/copiableField';
import type { CCMemberType, ProposalType } from '@src/models/types';
import { hexToBech32 } from '@src/utils/stringUtils';
import React from 'react';

export default function NewCommitteeParameters({
  proposal,
}: {
  proposal: ProposalType;
}) {
  const membersToBeAdded = ((proposal.details?.members as CCMemberType[]) || [])
    .filter((member) => member.newExpirationEpoch === undefined)
    .map((member) => ({
      cip129Identifier: hexToBech32(
        (member.hasScript ? '02' : '13') + member.hash,
        member.hasScript ? 'cc_hot' : 'cc_cold'
      ),
      expirationEpoch: member.expirationEpoch,
    }));

  const membersToBeUpdated = (
    (proposal.details?.members as CCMemberType[]) || []
  )
    .filter((member) => member.newExpirationEpoch !== undefined)
    .map((member) => ({
      cip129Identifier: hexToBech32(
        (member.hasScript ? '02' : '13') + member.hash,
        member.hasScript ? 'cc_hot' : 'cc_cold'
      ),
      expirationEpoch: member.expirationEpoch,
      newExpirationEpoch: member.newExpirationEpoch,
    }));
  if (proposal.details)
    return (
      <React.Fragment>
        {membersToBeAdded.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="body16 font-semibold dark:text-white">
              Governance Action members to be added
            </p>
            {membersToBeAdded.map(({ cip129Identifier }) => (
              <p
                key={cip129Identifier}
                className="body14 whitespace-pre-line text-gray-400"
              >
                {cip129Identifier}
              </p>
            ))}
          </div>
        )}
        {proposal.details.membersToBeRemoved?.length !== 0 && (
          <div className="flex flex-col gap-1">
            <p className="body16 font-semibold dark:text-white">
              Governance Action members to be removed
            </p>
            {proposal.details.membersToBeRemoved.map((hash: string) => (
              <CopyableField
                key={hash}
                value={hexToBech32(hash, 'cc_cold')}
                iconClassName="!h-4 !w-4"
                className="gap-2"
                textOnly
                textClassName="body14 text-gray-400 max-w-[210px] sm:max-w-[300px] xl:max-w-[500px]"
              />
            ))}
          </div>
        )}
        {membersToBeUpdated.length > 0 && (
          <div className="flex flex-col gap-1">
            <p className="body16 font-semibold dark:text-white">
              Change to terms of existing members
            </p>
            {membersToBeUpdated.map(
              ({ cip129Identifier, newExpirationEpoch, expirationEpoch }) => (
                <div
                  key={cip129Identifier}
                  className="body14 flex items-center gap-2"
                >
                  <p className="text-gray-400">
                    {expirationEpoch
                      ? `From ${expirationEpoch} to ${newExpirationEpoch} epoch`
                      : `To ${newExpirationEpoch} epoch`}
                  </p>
                  <CopyableField
                    value={cip129Identifier}
                    iconClassName="!h-4 !w-4"
                    className="gap-2"
                    textOnly
                    isPreCopyIcon
                    textClassName="body14 text-gray-400 max-w-[210px] sm:max-w-[300px] xl:max-w-[500px]"
                  />
                </div>
              )
            )}
          </div>
        )}
        {proposal.details.threshold && (
          <div className="flex items-center justify-between gap-2">
            <p className="body16 font-semibold dark:text-white">
              New threshold value
            </p>
            <p className="body14 text-gray-400">{proposal.details.threshold}</p>
          </div>
        )}
      </React.Fragment>
    );
  return null;
}
